import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody, Button, TextField, Grid, Card, CardContent, TableContainer } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import expenseService from '../../services/expenseService';
import { format, parseISO, isValid } from 'date-fns';
import { formatDate, formatTime } from '../../utils/dateFormatter';
import { useSnackbar } from 'notistack';
import { styled } from '@mui/material/styles';
import Layout from '../../components/layout/Layout';
import * as XLSX from 'xlsx';

const StyledTableHeadCell = styled(TableCell)(({ theme }) => ({
  padding: '10px 12px',
  border: `1px solid ${theme.palette.primary.light}`,
  backgroundColor: theme.palette.primary.dark,
  color: 'white',
  fontWeight: 'bold',
  fontSize: '13px',
  textAlign: 'center',
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: '8px 12px',
  border: '1px solid #e0e0e0',
  fontSize: '13px',
}));

const ExpenseViewPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const [loading, setLoading] = useState(false);
  const [allEntries, setAllEntries] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [applyTrigger, setApplyTrigger] = useState(0);

  useEffect(() => {
    setFromDate(todayStr);
    setToDate(todayStr);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await expenseService.listExpenses({ page: 0, size: 1000, fromDate, toDate });
        const list = Array.isArray(res) ? res : (res?.content || []);
        setAllEntries(list);
      } catch (e) {
        enqueueSnackbar(e.response?.data?.message || 'Failed to load expenses', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [enqueueSnackbar, applyTrigger]);

  const flatRows = useMemo(() => {
    const rows = [];
    allEntries.forEach(entry => {
      const getDateString = () => {
        if (typeof entry.entryDate === 'string' && entry.entryDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return entry.entryDate;
        }
        try {
          const d = typeof entry.entryDate === 'string' ? parseISO(entry.entryDate) : new Date(entry.entryDate);
          return isValid(d) ? format(d, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');
        } catch {
          return format(new Date(), 'yyyy-MM-dd');
        }
      };
      const dateStr = getDateString();
      const extractTimeString = () => {
        if (!entry.createdAt) return '';
        return formatTime(entry.createdAt);
      };
      const timeStr = extractTimeString();
      if (fromDate && dateStr < fromDate) return;
      if (toDate && dateStr > toDate) return;
      const items = Array.isArray(entry.items) ? entry.items : (entry.details || []);
      (items || []).forEach(it => {
        rows.push({
          dateTime: timeStr ? `${dateStr} (${timeStr})` : dateStr,
          reason: it.reason || it.type || 'OTHER',
          description: it.description || '',
          amount: parseFloat(it.amount) || 0
        });
      });
    });
    return rows;
  }, [allEntries, fromDate, toDate]);

  const totalExpense = useMemo(() => flatRows.reduce((sum, r) => sum + (r.amount || 0), 0), [flatRows]);
  const handleApplyFilter = () => {
    setApplyTrigger(prev => prev + 1);
  };
  const handleExportToExcel = async () => {
    try {
      const pageSize = 1000;
      let pageIndex = 0;
      let totalPages = 1;
      const allForExport = [];
      do {
        const res = await expenseService.listExpenses({ page: pageIndex, size: pageSize, fromDate, toDate });
        const content = Array.isArray(res) ? res : (res?.content || []);
        allForExport.push(...content);
        totalPages = res?.totalPages ?? (content.length < pageSize ? pageIndex + 1 : pageIndex + 2);
        pageIndex++;
      } while (pageIndex < totalPages);
      const exportRows = [];
      allForExport.forEach(entry => {
        const getDateString = () => {
          if (typeof entry.entryDate === 'string' && entry.entryDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
            return entry.entryDate;
          }
          try {
            const d = typeof entry.entryDate === 'string' ? parseISO(entry.entryDate) : new Date(entry.entryDate);
            return isValid(d) ? format(d, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');
          } catch {
            return format(new Date(), 'yyyy-MM-dd');
          }
        };
        const dateStr = getDateString();
        const extractTimeString = () => {
          if (!entry.createdAt) return '';
          if (typeof entry.createdAt === 'string') {
            const s = entry.createdAt.replace('T', ' ');
            const m = s.match(/\b(\d{2}):(\d{2})(?::\d{2})?/);
            if (m) {
              const hh = parseInt(m[1], 10);
              const mm = m[2];
              const ampm = hh >= 12 ? 'PM' : 'AM';
              const h12 = ((hh + 11) % 12) + 1;
              return `${String(h12).padStart(2, '0')}:${mm} ${ampm}`;
            }
            return '';
          }
          try {
            const d = new Date(entry.createdAt);
            if (isValid(d)) {
              return format(d, 'hh:mm a');
            }
          } catch { }
          return '';
        };
        const timeStr = extractTimeString();
        if (fromDate && dateStr < fromDate) return;
        if (toDate && dateStr > toDate) return;
        const items = Array.isArray(entry.items) ? entry.items : (entry.details || []);
        (items || []).forEach(it => {
          exportRows.push({
            DateTime: timeStr ? `${dateStr} (${timeStr})` : dateStr,
            Reason: it.reason || it.type || 'OTHER',
            Description: it.description || '',
            Amount: parseFloat(it.amount) || 0
          });
        });
      });
      exportRows.push({
        DateTime: '',
        Reason: 'TOTAL',
        Description: '',
        Amount: exportRows.reduce((s, r) => s + (r.Amount || 0), 0)
      });
      const ws = XLSX.utils.json_to_sheet(exportRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Expenses');
      XLSX.writeFile(wb, `expenses_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
    } catch (e) {
      enqueueSnackbar('Failed to export expenses', { variant: 'error' });
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3, width: '100%' }}>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          View Expenses
        </Typography>

        {/* Summary and Filters */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ background: 'linear-gradient(135deg, #42a5f5 0%, #1e88e5 50%, #1565c0 100%)', color: 'white', border: 'none' }}>
              <CardContent>
                <Typography variant="subtitle2">Total Expense</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{`₹${totalExpense.toFixed(2)}`}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    type="date"
                    label="From"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    inputProps={{ max: todayStr }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    type="date"
                    label="To"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    inputProps={{ max: todayStr }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
          <Button variant="contained" onClick={handleApplyFilter} sx={{ height: 40, fontWeight: 600 }}>
            Apply Filter
          </Button>
          <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExportToExcel} sx={{ height: 40, fontWeight: 600 }}>
            Export
          </Button>
        </Box>

        {/* Flat Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableHeadCell>Date</StyledTableHeadCell>
                <StyledTableHeadCell>Reason</StyledTableHeadCell>
                <StyledTableHeadCell>Description</StyledTableHeadCell>
                <StyledTableHeadCell>Amount</StyledTableHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {flatRows.map((r, idx) => (
                <TableRow key={idx}>
                  <StyledTableCell>{r.dateTime}</StyledTableCell>
                  <StyledTableCell>{r.reason}</StyledTableCell>
                  <StyledTableCell>{r.description}</StyledTableCell>
                  <StyledTableCell align="right">{`₹${(r.amount || 0).toFixed(2)}`}</StyledTableCell>
                </TableRow>
              ))}
              {!loading && flatRows.length === 0 && (
                <TableRow>
                  <StyledTableCell colSpan={4} align="center">No expenses found</StyledTableCell>
                </TableRow>
              )}
              {flatRows.length > 0 && (
                <TableRow>
                  <StyledTableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>Total</StyledTableCell>
                  <StyledTableCell align="right" sx={{ fontWeight: 'bold' }}>{`₹${totalExpense.toFixed(2)}`}</StyledTableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Layout>
  );
};

export default ExpenseViewPage;
